// =============================================================================
// src/engine/chordDetect.ts
// Identifies a chord name from a set of MIDI pitches.
//
// Algorithm:
//   1. Collect unique pitch classes from the selected strings (DEDUPLICATED).
//   2. For every possible root (each pitch class present), normalise the
//      interval set relative to that root.
//   3. Look up the interval set in a built-in chord dictionary.
//   4. Among all matches, prefer root-position (lowest note = root).
//   5. If lowest note ≠ root → slash chord (e.g. "G7/B").
//
// Full chord coverage: major, minor, aug, dim, sus2, sus4, add9, add2,
//                     6, dom7, maj7, min7, dim7, m7b5, 9, maj9, m9, etc.
// =============================================================================

import type { ApplyResult, ChordResult } from "./types";
import { intervalMod12, midiToNoteClass } from "./pitch";

// ---------------------------------------------------------------------------
// Chord dictionary: interval-set signature → quality label
// Each entry: sorted semitone intervals from root, root (0) always included.
// ---------------------------------------------------------------------------

interface ChordEntry {
  intervals: number[];   // sorted, root=0 included
  quality: string;       // display quality, e.g. "maj7"
  priority: number;      // tie-break: lower = preferred
}

const CHORD_DICT: ChordEntry[] = [
  // ===== TRIADS =====
  { intervals: [0, 4, 7], quality: "maj", priority: 1 },
  { intervals: [0, 3, 7], quality: "m", priority: 1 },
  { intervals: [0, 3, 6], quality: "dim", priority: 2 },
  { intervals: [0, 4, 8], quality: "aug", priority: 2 },
  
  // ===== SUSPENDED =====
  { intervals: [0, 2, 7], quality: "sus2", priority: 3 },
  { intervals: [0, 5, 7], quality: "sus4", priority: 3 },
  
  // ===== POWER CHORD =====
  { intervals: [0, 7], quality: "5", priority: 4 },
  
  // ===== ADD CHORDS (No 7th) - CRITICAL for steel guitar =====
  { intervals: [0, 2, 4, 7], quality: "add9", priority: 2 },   // E F# G# B = Eadd9
  { intervals: [0, 2, 3, 7], quality: "madd9", priority: 2 },  // Minor add9
  { intervals: [0, 4, 5, 7], quality: "add4", priority: 3 },   // Add4
  { intervals: [0, 3, 5, 7], quality: "madd4", priority: 3 },  // Minor add4
  
  // ===== SIXTH CHORDS =====
  { intervals: [0, 4, 7, 9], quality: "6", priority: 2 },
  { intervals: [0, 3, 7, 9], quality: "m6", priority: 2 },
  { intervals: [0, 2, 4, 7, 9], quality: "6/9", priority: 3 }, // Major 6/9
  
  // ===== SEVENTH CHORDS =====
  { intervals: [0, 4, 7, 10], quality: "7", priority: 1 },      // Dominant 7
  { intervals: [0, 4, 7, 11], quality: "maj7", priority: 1 },   // Major 7
  { intervals: [0, 3, 7, 10], quality: "m7", priority: 1 },     // Minor 7
  { intervals: [0, 3, 7, 11], quality: "mMaj7", priority: 2 },  // Minor-Major 7
  { intervals: [0, 3, 6, 9], quality: "dim7", priority: 2 },    // Diminished 7
  { intervals: [0, 3, 6, 10], quality: "m7b5", priority: 2 },   // Half-diminished
  { intervals: [0, 4, 8, 10], quality: "aug7", priority: 3 },   // Augmented 7
  
  // ===== SUSPENDED 7TH =====
  { intervals: [0, 5, 7, 10], quality: "7sus4", priority: 3 },
  { intervals: [0, 2, 7, 10], quality: "7sus2", priority: 3 },
  
  // ===== NINTH CHORDS =====
  { intervals: [0, 2, 4, 7, 10], quality: "9", priority: 3 },   // Dominant 9
  { intervals: [0, 2, 4, 7, 11], quality: "maj9", priority: 3 },// Major 9  
  { intervals: [0, 2, 3, 7, 10], quality: "m9", priority: 3 },  // Minor 9
  
  // ===== PARTIAL CHORDS (common voicings without 3rd) =====
  { intervals: [0, 7, 10], quality: "7no3", priority: 4 },      // Dom7 no 3rd
  { intervals: [0, 7, 11], quality: "maj7no3", priority: 4 },   // Maj7 no 3rd
  { intervals: [0, 2, 7, 10], quality: "9no3", priority: 4 },   // 9 no 3rd (same as 7sus2)
  
  // ===== ADD9 variations (common steel voicings) =====
  { intervals: [0, 2, 7], quality: "add2no3", priority: 4 },    // sus2 technically
  { intervals: [0, 4, 9], quality: "6no5", priority: 4 },       // 6th without 5th
  { intervals: [0, 2, 4], quality: "add9no5", priority: 5 },    // 1-2-3 cluster
];

// Precompute string keys for fast lookup
const DICT_MAP = new Map<string, ChordEntry>();
for (const entry of CHORD_DICT) {
  DICT_MAP.set(JSON.stringify(entry.intervals), entry);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Normalise a set of pitch classes to sorted unique intervals relative to a
 *  given root pitch class, all mod 12. */
function intervalsRelativeTo(pitchClasses: number[], rootPitchClass: number): number[] {
  const set = new Set(pitchClasses.map(pc => intervalMod12(rootPitchClass, pc)));
  return [...set].sort((a, b) => a - b);
}

/** Build a chord symbol string from root + quality. */
function buildSymbol(root: string, quality: string): string {
  // "maj" becomes just the root letter(s), all others appended.
  return quality === "maj" ? root : `${root}${quality}`;
}

/** Extract unique pitch classes from MIDI notes (deduplicated, mod 12) */
function extractUniquePitchClasses(midiNotes: number[]): number[] {
  const pitchClassSet = new Set(midiNotes.map(m => ((m % 12) + 12) % 12));
  return [...pitchClassSet].sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Identify a chord from a subset of strings in an ApplyResult.
 * 
 * CRITICAL FIXES:
 * 1. Normalizes notes to pitch classes and DEDUPLICATES (handles duplicate notes across strings)
 * 2. Tries EVERY pitch class as potential root (not just lowest note)
 * 3. Includes add9, add2, 6, and other steel-friendly voicings
 * 4. Fast operation for rapid clicking / pedal toggling
 *
 * @param applied        The resolved string states (from applyAtFret).
 * @param stringIndexes  Which strings (0-based) to include in the chord.
 * @param preferSharps   Whether to display note names using sharps.
 * @returns              ChordResult, or null if no chord matched.
 */
export function chordFromStrings(
  applied: ApplyResult,
  stringIndexes: number[],
  preferSharps = true
): ChordResult | null {
  if (stringIndexes.length < 2) return null;

  // Gather MIDI notes for selected strings
  const selected = stringIndexes
    .map(i => applied.strings[i])
    .filter(Boolean);

  if (selected.length < 2) return null;

  const midiNotes = selected.map(s => s.effectiveMidi);
  const noteNames = selected.map(s => s.noteName);

  // CRITICAL: Deduplicate pitch classes (handles duplicate notes across strings)
  // E.g., E2, E3, B2, F#4, G# -> pitch classes {E, F#, G#, B}
  const uniquePitchClasses = extractUniquePitchClasses(midiNotes);
  
  // Need at least 2 unique pitch classes to form a chord
  if (uniquePitchClasses.length < 2) return null;

  // The lowest MIDI note is the bass note (for inversion detection)
  const bassMidi = Math.min(...midiNotes);
  const bassPitchClass = ((bassMidi % 12) + 12) % 12;

  // --- Try every unique pitch class as a candidate root ---
  interface Candidate {
    rootPitchClass: number;
    entry: ChordEntry;
    inversion: number;
  }
  const candidates: Candidate[] = [];

  for (const rootPC of uniquePitchClasses) {
    const intervals = intervalsRelativeTo(uniquePitchClasses, rootPC);
    const key = JSON.stringify(intervals);
    const entry = DICT_MAP.get(key);
    if (!entry) continue;

    // Inversion index: 0 if bass == root, else calculate
    const inversion = rootPC === bassPitchClass ? 0 : 
      (uniquePitchClasses.indexOf(bassPitchClass) + uniquePitchClasses.length - uniquePitchClasses.indexOf(rootPC)) % uniquePitchClasses.length;

    candidates.push({ rootPitchClass: rootPC, entry, inversion });
  }

  if (candidates.length === 0) return null;

  // --- Rank candidates ---
  // Prefer: root position first, then lower priority number, then rootPC order
  candidates.sort((a, b) => {
    if (a.inversion !== b.inversion) return a.inversion - b.inversion;
    if (a.entry.priority !== b.entry.priority) return a.entry.priority - b.entry.priority;
    return a.rootPitchClass - b.rootPitchClass;
  });

  const best = candidates[0];
  const rootName = midiToNoteClass(best.rootPitchClass, preferSharps);
  const symbol = buildSymbol(rootName, best.entry.quality);

  // Slash chord if bass ≠ root
  const isSlash = best.inversion !== 0;
  const bassName = isSlash ? midiToNoteClass(bassPitchClass, preferSharps) : null;
  const fullSymbol = isSlash ? `${symbol}/${bassName}` : symbol;

  return {
    symbol: fullSymbol,
    root: rootName,
    quality: best.entry.quality,
    bassNote: bassName,
    midiNotes,
    noteNames,
    intervals: intervalsRelativeTo(uniquePitchClasses, best.rootPitchClass),
    inversion: best.inversion,
  };
}

/**
 * Direct chord detection from pitch classes (note names without octaves).
 * Useful for testing or when you only have pitch class information.
 * 
 * @param pitchClassNames Array of note names like ['E', 'F#', 'G#', 'B']
 * @param preferSharps Whether to display note names using sharps
 * @returns ChordResult, or null if no chord matched
 */
export function detectChordFromPitchClasses(
  pitchClassNames: string[],
  preferSharps = true
): ChordResult | null {
  if (pitchClassNames.length < 2) return null;

  // Convert note names to pitch classes (0-11)
  const NOTE_TO_PC: Record<string, number> = {
    'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3,
    'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8,
    'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11
  };

  const pitchClasses: number[] = [];
  for (const name of pitchClassNames) {
    const normalized = name.trim();
    const pc = NOTE_TO_PC[normalized];
    if (pc !== undefined) {
      pitchClasses.push(pc);
    }
  }

  if (pitchClasses.length < 2) return null;

  // Deduplicate
  const uniquePitchClasses = [...new Set(pitchClasses)].sort((a, b) => a - b);
  if (uniquePitchClasses.length < 2) return null;

  // Use lowest pitch class as bass (for this simplified version)
  const bassPitchClass = uniquePitchClasses[0];

  // Try every pitch class as candidate root
  interface Candidate {
    rootPitchClass: number;
    entry: ChordEntry;
    inversion: number;
  }
  const candidates: Candidate[] = [];

  for (const rootPC of uniquePitchClasses) {
    const intervals = intervalsRelativeTo(uniquePitchClasses, rootPC);
    const key = JSON.stringify(intervals);
    const entry = DICT_MAP.get(key);
    if (!entry) continue;

    const inversion = rootPC === bassPitchClass ? 0 : 
      (uniquePitchClasses.indexOf(bassPitchClass) + uniquePitchClasses.length - uniquePitchClasses.indexOf(rootPC)) % uniquePitchClasses.length;

    candidates.push({ rootPitchClass: rootPC, entry, inversion });
  }

  if (candidates.length === 0) return null;

  // Rank: root position first, then priority, then rootPC
  candidates.sort((a, b) => {
    if (a.inversion !== b.inversion) return a.inversion - b.inversion;
    if (a.entry.priority !== b.entry.priority) return a.entry.priority - b.entry.priority;
    return a.rootPitchClass - b.rootPitchClass;
  });

  const best = candidates[0];
  const rootName = midiToNoteClass(best.rootPitchClass, preferSharps);
  const symbol = buildSymbol(rootName, best.entry.quality);

  const isSlash = best.inversion !== 0;
  const bassName = isSlash ? midiToNoteClass(bassPitchClass, preferSharps) : null;
  const fullSymbol = isSlash ? `${symbol}/${bassName}` : symbol;

  return {
    symbol: fullSymbol,
    root: rootName,
    quality: best.entry.quality,
    bassNote: bassName,
    midiNotes: [], // Not available in this mode
    noteNames: pitchClassNames,
    intervals: intervalsRelativeTo(uniquePitchClasses, best.rootPitchClass),
    inversion: best.inversion,
  };
}
